import React, { useState, useEffect, useCallback, useMemo } from 'react';

// --- CLAVE DE ALMACENAMIENTO LOCAL ---
const STORAGE_KEY = 'lab_results_data';

const initialLabState = {
    id: null,
    paciente: '', 
    examen: '', 
    fecha_resultado: new Date().toISOString().substring(0, 10), 
    hallazgos: ''
};

// --- FUNCIONES DE ALMACENAMIENTO LOCAL ---
const loadData = () => {
    try {
        const storedData = localStorage.getItem(STORAGE_KEY);
        return storedData ? JSON.parse(storedData) : [];
    } catch (error) {
        console.error("Error al cargar datos desde LocalStorage. Devolviendo array vacío.", error);
        return [];
    }
};

const saveData = (data) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
        console.error("Error al guardar datos en LocalStorage:", error);
    }
};

// =================================================================
// 0. ICONOS MODERNOS (Lucide Icons como componentes React)
// =================================================================

const PlusCircle = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v8" />
        <path d="M8 12h8" />
    </svg>
);

const Search = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
    </svg>
);

const Pencil = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
);

const Trash2 = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M3 6h18" />
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
        <line x1="10" x2="10" y1="11" y2="17" />
        <line x1="14" x2="14" y1="11" y2="17" />
    </svg>
);

const Printer = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M6 18H4c-1.1 0-2-.9-2-2V9c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v7c0 1.1-.9 2-2 2h-2" />
        <path d="M18 14H6V4h12v10Z" />
        <path d="M14 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
    </svg>
);

const AlertTriangle = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="m21.73 18.04-8.87-15.04a2 2 0 0 0-3.72 0L2.27 18.04A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-2.96Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
    </svg>
);


// =================================================================
// 1. Componente del Reporte Imprimible (Diseño Profesional)
// =================================================================

const PrintableReport = ({ labResult }) => {
    if (!labResult) return null;

    return (
        <div id="printable-report" className="bg-white p-10 w-full mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            
            {/* Encabezado del Reporte */}
            <header className="border-b-4 border-teal-700 pb-4 mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-extrabold text-teal-900">LABORATORIO CLÍNICO EXCELENCIA</h1>
                        <p className="text-gray-600 text-sm">Reporte de Resultados Oficial</p>
                    </div>
                    {/* Información de la clínica (simulada) */}
                    <div className="text-xs text-right">
                        <p className="font-semibold text-gray-800">Dirección: Av. Principal #123</p>
                        <p className="text-gray-500">ID de Reporte: <span className="font-mono">{labResult.id.substring(0, 8)}</span></p>
                    </div>
                </div>
            </header>

            {/* Información del Paciente y Estudio */}
            <section className="mb-8 border-b pb-4 bg-indigo-50 p-4 rounded-lg">
                <h2 className="text-xl font-semibold text-indigo-800 mb-3">Datos del Paciente y Estudio</h2>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <p><span className="font-medium text-gray-600">Paciente:</span> <span className="text-gray-900 font-bold uppercase">{labResult.paciente}</span></p>
                    <p><span className="font-medium text-gray-600">Fecha de Resultado:</span> {labResult.fecha_resultado}</p>
                    <p><span className="font-medium text-gray-600">Tipo de Examen:</span> {labResult.examen}</p>
                </div>
            </section>

            {/* Sección de Hallazgos y Observaciones */}
            <section className="mb-8">
                <h2 className="text-2xl font-bold text-teal-700 mb-4 border-l-4 border-teal-500 pl-3">Resultados y Hallazgos Clínicos</h2>
                
                <div className="bg-white p-6 rounded-lg border border-gray-300 min-h-[300px] whitespace-pre-wrap break-words shadow-md">
                    <p className="text-gray-900 text-base leading-relaxed">
                        {labResult.hallazgos || 'No se registraron hallazgos detallados.'}
                    </p>
                </div>
            </section>

            {/* Firma y pie de página */}
            <section className="mt-10 pt-4 border-t border-gray-300">
                <div className="flex justify-between items-end mt-8">
                    <div>
                        <div className="border-t border-gray-500 w-48 pt-2"></div>
                        <p className="text-sm font-bold text-gray-800">Dr(a). Nombre del Analista Clínico</p>
                        <p className="text-xs text-gray-500">Cédula Profesional: 123456</p>
                    </div>
                    <p className="text-xs text-gray-500">Generado el: {new Date().toLocaleDateString('es-ES')}</p>
                </div>
                <div className="text-center mt-4">
                    <p className="text-xs text-gray-400">Documento no válido sin firma y sello oficial.</p>
                </div>
            </section>
        </div>
    );
};


// =================================================================
// 2. Componente Modal CRUD
// =================================================================

const CrudModal = React.memo(({
    isModalOpen,
    isEditing,
    currentLaboratorio,
    handleModalChange,
    handleSubmit,
    closeModal
}) => {
    if (!isModalOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-8 transform scale-100 transition-all duration-300">
                <h3 className="text-2xl font-bold mb-6 text-indigo-700 border-b pb-2 flex items-center">
                    <Pencil className="w-6 h-6 mr-3 text-indigo-500" />
                    {isEditing ? 'Editar Resultado Existente' : 'Registrar Nuevo Resultado'}
                </h3>
                
                <form onSubmit={handleSubmit}>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        
                        {/* Campo Paciente */}
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-semibold mb-2">Paciente:</label>
                            <input
                                type="text"
                                name="paciente"
                                required
                                value={currentLaboratorio.paciente}
                                onChange={handleModalChange} 
                                className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                placeholder="Nombre completo"
                                autoFocus={!isEditing}
                            />
                        </div>
                        
                        {/* Campo Examen */}
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-semibold mb-2">Tipo de Examen:</label>
                            <input
                                type="text"
                                name="examen"
                                required
                                value={currentLaboratorio.examen}
                                onChange={handleModalChange}
                                className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                placeholder="Ej: Química Sanguínea"
                            />
                        </div>

                        {/* Campo Fecha de Resultado */}
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-semibold mb-2">Fecha de Resultado:</label>
                            <input
                                type="date"
                                name="fecha_resultado"
                                required
                                value={currentLaboratorio.fecha_resultado}
                                onChange={handleModalChange}
                                className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                            />
                        </div>
                    </div>

                    {/* Campo Hallazgos */}
                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-semibold mb-2">Hallazgos y Observaciones:</label>
                        <textarea
                            name="hallazgos"
                            required
                            rows="8"
                            value={currentLaboratorio.hallazgos}
                            onChange={handleModalChange}
                            className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                            placeholder="Detalle los resultados, valores de referencia y conclusiones médicas de manera profesional."
                        ></textarea>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-end space-x-4 pt-4 border-t">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-300 transition duration-150 shadow-md flex items-center"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="bg-indigo-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:bg-indigo-700 transition duration-150 flex items-center"
                        >
                            {isEditing ? 'Guardar Cambios' : 'Registrar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
});


// =================================================================
// 3. Componente Modal de Confirmación de Eliminación
// =================================================================

const DeleteConfirmationModal = React.memo(({
    isDeleteModalOpen, 
    currentLaboratorio, 
    confirmDelete, 
    closeDeleteModal
}) => {
    if (!isDeleteModalOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
                <div className="text-center">
                    <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
                    <h3 className="text-xl font-bold leading-6 text-gray-900 mt-4">Confirmar Eliminación</h3>
                    <p className="text-sm text-gray-500 mt-2">
                        Estás a punto de eliminar permanentemente el resultado del examen 
                        <strong className="text-red-600"> {currentLaboratorio.examen}</strong> 
                        para <strong className="font-semibold">{currentLaboratorio.paciente}</strong>. Esta acción es irreversible.
                    </p>
                </div>
                
                <div className="mt-5 sm:mt-6 flex justify-between space-x-4">
                    <button
                        type="button"
                        onClick={closeDeleteModal}
                        className="inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 transition duration-150"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={confirmDelete}
                        className="inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 transition duration-150 flex items-center"
                    >
                        <Trash2 className="w-5 h-5 mr-1" />
                        Sí, Eliminar
                    </button>
                </div>
            </div>
        </div>
    );
});


// =================================================================
// 4. Componente Principal (Lógica de la Aplicación)
// =================================================================

const App = () => {
    // --- ESTADO DE LA APLICACIÓN ---
    const [laboratorios, setLaboratorios] = useState(loadData);
    const [searchQuery, setSearchQuery] = useState('');
    const [message, setMessage] = useState('');
    
    // --- ESTADO DE MODALES Y EDICIÓN ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentLaboratorio, setCurrentLaboratorio] = useState(initialLabState);
    
    // --- ESTADO DE IMPRESIÓN ---
    const [currentLaboratorioToPrint, setCurrentLaboratorioToPrint] = useState(null);

    // --- EFECTO: Sincronización con LocalStorage ---
    useEffect(() => {
        saveData(laboratorios);
    }, [laboratorios]);

    // --- LÓGICA DE UI Y UTILIDAD ---
    const showNotification = (msg, isError = false) => {
        setMessage(msg);
        setTimeout(() => setMessage(''), 3000);
        if (isError) console.error(msg);
    };

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setIsEditing(false);
        setCurrentLaboratorio(initialLabState);
    }, []);

    const closeDeleteModal = useCallback(() => {
        setIsDeleteModalOpen(false);
        setCurrentLaboratorio(initialLabState);
    }, []);
    
    const handleModalChange = useCallback((e) => {
        const { name, value } = e.target;
        setCurrentLaboratorio(prev => ({
            ...prev,
            [name]: value
        }));
    }, [setCurrentLaboratorio]);

    // --- MANEJADOR DE IMPRESIÓN ---
    const handlePrint = useCallback((lab) => {
        setCurrentLaboratorioToPrint(lab);
        
        // Esperar al siguiente ciclo de evento para que React renderice el PrintableReport
        setTimeout(() => {
            window.print();
            
            // Limpiar el estado de impresión después de un breve retraso
            setTimeout(() => {
                setCurrentLaboratorioToPrint(null);
            }, 500);
        }, 0); 
    }, []);


    // --- CÁLCULO DE DATOS (Memoized) ---
    const filteredLaboratorios = useMemo(() => {
        // Ordenar primero por fecha de resultado, el más reciente al inicio
        const sortedData = [...laboratorios].sort((a, b) => 
            new Date(b.fecha_resultado).getTime() - new Date(a.fecha_resultado).getTime()
        );
        
        const query = searchQuery.toLowerCase().trim();
        if (!query) {
            return sortedData;
        }

        return sortedData.filter(lab => 
            lab.paciente.toLowerCase().includes(query) ||
            lab.examen.toLowerCase().includes(query) ||
            lab.hallazgos.toLowerCase().includes(query) ||
            lab.fecha_resultado.includes(query) // Búsqueda por año/mes
        );
    }, [laboratorios, searchQuery]);

    // --- MANEJADORES CRUD (Memoizados) ---

    const handleCreate = useCallback(() => {
        setIsEditing(false);
        setCurrentLaboratorio({ 
            ...initialLabState, 
            fecha_resultado: new Date().toISOString().substring(0, 10),
        });
        setIsModalOpen(true);
    }, []);

    const handleEdit = useCallback((lab) => {
        setIsEditing(true);
        setCurrentLaboratorio(lab); 
        setIsModalOpen(true);
    }, []);

    const handleDelete = useCallback((lab) => {
        setCurrentLaboratorio(lab);
        setIsDeleteModalOpen(true);
    }, []);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        
        if (!currentLaboratorio.paciente || !currentLaboratorio.examen || !currentLaboratorio.fecha_resultado || !currentLaboratorio.hallazgos) {
            showNotification("[ERROR] Faltan campos obligatorios.", true);
            return;
        }
        
        setLaboratorios(prevLabs => {
            if (isEditing) {
                // UPDATE
                return prevLabs.map(lab => 
                    lab.id === currentLaboratorio.id ? currentLaboratorio : lab
                );
            } else {
                // CREATE (Añadir un nuevo ID único)
                const newLab = {
                    ...currentLaboratorio,
                    id: crypto.randomUUID() // Generamos el ID
                };
                return [...prevLabs, newLab];
            }
        });
        
        showNotification(`[${isEditing ? 'ACTUALIZADO' : 'CREADO'}] Resultado de ${currentLaboratorio.examen} guardado con éxito.`);
        closeModal();
    }, [isEditing, currentLaboratorio, closeModal]);

    const confirmDelete = useCallback(() => {
        if (!currentLaboratorio.id) {
            showNotification("[ERROR] No se pudo obtener la referencia del documento.", true);
            closeDeleteModal();
            return;
        }

        setLaboratorios(prevLabs => prevLabs.filter(lab => lab.id !== currentLaboratorio.id));
        
        showNotification(`[ELIMINADO] Resultado de ${currentLaboratorio.examen} eliminado.`);
        
        closeDeleteModal();
    }, [currentLaboratorio, closeDeleteModal]);
    
    // --- RENDERING (VISTA) ---

    // Componente de Notificación Flotante
    const Notification = () => (
        <div 
            className={`fixed top-0 left-1/2 transform -translate-x-1/2 mt-4 z-50 transition-opacity duration-500 ${message ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
            <div className={`text-white font-bold py-2 px-4 rounded-lg shadow-xl ${message.includes('[ERROR]') ? 'bg-red-600' : 'bg-green-600'} animate-pulse`}>
                {message}
            </div>
        </div>
    );

    // Componente de Mensaje de No Resultados
    const NoResults = () => (
        <div className="bg-gray-100 p-8 text-center rounded-xl shadow-inner border border-dashed border-gray-300">
            <h3 className="text-xl font-bold text-gray-500 mb-2">¡Sin Resultados Encontrados!</h3>
            <p className="text-gray-500 font-medium">
                No hay registros de resultados que coincidan con su búsqueda. 
                {laboratorios.length === 0 && ' Utilice el botón "Registrar Nuevo Resultado" para agregar el primer examen.'}
            </p>
        </div>
    );
    
    // Componente de la Tabla de Resultados 
    const LaboratoriosTable = ({ data }) => (
        <div className="overflow-x-auto shadow-xl rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-indigo-100">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">ID</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Paciente</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Examen</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Fecha</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Hallazgos</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-indigo-700 uppercase tracking-wider">Acciones</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                    {data.map(lab => (
                        <tr key={lab.id} className="hover:bg-indigo-50 transition duration-100">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{lab.id.substring(0, 4)}...</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">{lab.paciente}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lab.examen}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lab.fecha_resultado}</td>
                            <td className="px-6 py-4 max-w-xs overflow-hidden text-ellipsis text-sm text-gray-600">
                                {lab.hallazgos.substring(0, 50)}{lab.hallazgos.length > 50 ? '...' : ''}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                <button
                                    onClick={() => handlePrint(lab)} 
                                    title="Generar PDF/Imprimir"
                                    className="text-teal-600 hover:text-teal-800 p-2 rounded-full hover:bg-teal-100 transition duration-150"
                                >
                                    <Printer className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => handleEdit(lab)} 
                                    title="Editar Registro"
                                    className="text-indigo-600 hover:text-indigo-800 p-2 rounded-full hover:bg-indigo-100 transition duration-150"
                                >
                                    <Pencil className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => handleDelete(lab)} 
                                    title="Eliminar Registro"
                                    className="text-red-600 hover:text-red-800 p-2 rounded-full hover:bg-red-100 transition duration-150"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // --- ESTILOS DE IMPRESIÓN ---
    const printStyles = `
        @media print {
            /* Ocultar la Interfaz de Usuario principal de la aplicación */
            #main-app-ui {
                display: none !important;
            }

            /* Mostrar y posicionar el contenedor del reporte */
            #printable-report-wrapper {
                display: block !important;
                position: fixed !important; 
                left: 0 !important;
                top: 0 !important;
                width: 100% !important;
                height: 100% !important;
                background: white !important;
                z-index: 9999 !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: visible !important;
            }

            body, html {
                margin: 0 !important;
                padding: 0 !important;
                background-color: white !important;
                visibility: visible !important; 
            }
            
            .whitespace-pre-wrap {
                white-space: pre-wrap !important;
                overflow: visible !important;
                word-break: break-word !important;
            }

            /* Definir márgenes de página */
            @page { 
                margin: 1cm;
            }
        }
        
        /* Por defecto, el componente de impresión está oculto */
        #printable-report-wrapper {
            display: none;
        }
    `;
    
    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen rounded-xl shadow-2xl">
            
            {/* ESTILOS DE IMPRESIÓN DINÁMICOS */}
            <style>{printStyles}</style>

            <Notification />
            
            <div id="main-app-ui">
                <h1 className="text-4xl font-extrabold text-indigo-800 mb-8 border-b-2 border-indigo-200 pb-3 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 text-teal-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13v6"/><path d="M12 16H9"/></svg>
                    Sistema de Resultados de Laboratorio
                </h1>
                
                {/* Controles: Botón Crear y Barra de Búsqueda */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
                    
                    {/* Botón para crear */}
                    <button
                        onClick={handleCreate} 
                        className="flex items-center w-full md:w-auto justify-center px-6 py-3 bg-teal-600 text-white font-bold rounded-xl shadow-lg hover:bg-teal-700 transition duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                        <PlusCircle className="h-5 w-5 mr-2" />
                        Registrar Nuevo Resultado
                    </button>
                    
                    {/* Barra de Búsqueda */}
                    <div className="relative w-full md:max-w-md">
                        <input
                            type="text"
                            placeholder="Buscar paciente, examen, fecha o hallazgos..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full py-3 pl-12 pr-4 border border-gray-300 rounded-xl shadow-md focus:outline-none focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition duration-150"
                        />
                        <Search className="h-6 w-6 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                </div>

                <h2 className="text-2xl font-semibold text-gray-700 mb-4 border-l-4 border-indigo-400 pl-3">
                    Resultados ({filteredLaboratorios.length} encontrados)
                </h2>

                {filteredLaboratorios.length === 0 && <NoResults />}
                
                {filteredLaboratorios.length > 0 && <LaboratoriosTable data={filteredLaboratorios} />}

            </div>
            
            {/* Contenedor del reporte imprimible (invisible por defecto) */}
            <div id="printable-report-wrapper">
                {currentLaboratorioToPrint && <PrintableReport labResult={currentLaboratorioToPrint} />}
            </div>

            <CrudModal 
                isModalOpen={isModalOpen}
                isEditing={isEditing}
                currentLaboratorio={currentLaboratorio}
                handleModalChange={handleModalChange}
                handleSubmit={handleSubmit}
                closeModal={closeModal}
            />
            
            <DeleteConfirmationModal 
                isDeleteModalOpen={isDeleteModalOpen}
                currentLaboratorio={currentLaboratorio}
                confirmDelete={confirmDelete}
                closeDeleteModal={closeDeleteModal}
            />

        </div>
    );
};

export default App;